import { Box, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styleVariables from '@/styles/style.module.scss';

export interface HistoryItemProps {
    eslMac: string;
    productBarcode?: string;
    status: 'success' | 'failed';
    timestamp: string;
    productName?: string;
    errorReason?: string;
    allSuccess?: boolean;
    containsEmptyProduct?: boolean;
    special_job_text?: string;
    other_info?: string;
    customMessage?: string;
}

interface HistoryProps {
    historyItems: HistoryItemProps[];
    addLeadingZero: boolean;
}

const History: React.FC<HistoryProps> = ({ historyItems, addLeadingZero }) => {
    const { t } = useTranslation();

    return (
        <Box>
            <Typography
                variant="body2"
                sx={{
                    fontWeight: 'bold',
                    mb: 0.75,
                    color: styleVariables.textColor,
                }}
            >
                {t('History')}
            </Typography>

            <Box
                data-testid="mobile-history-list"
                sx={{
                    p: 1.5,
                    height: 230,
                    overflowY: 'auto',
                    border: `1px solid ${styleVariables.secondaryColor}`, // 對應 variables.$secondary-color
                    borderRadius: 2,
                    '&::-webkit-scrollbar': { display: 'none' },
                    scrollbarWidth: 'none',
                }}
            >
                {historyItems.length > 0 ? (
                    <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
                        {historyItems.map((item, index) => {
                            const processedBarcode =
                                addLeadingZero && item.productBarcode && item.productBarcode.length < 13
                                    ? item.productBarcode.padStart(13, '0')
                                    : item.productBarcode;

                            const hasEmptyProduct = item.productName
                                ?.split(', ')
                                .some((name) => name === 'Empty Product');

                            let statusClass = '';
                            if (!item.productBarcode) {
                                statusClass = item.status === 'success' ? 'success' : 'failed';
                            } else {
                                if (typeof item.allSuccess === 'boolean') {
                                    statusClass = item.allSuccess
                                        ? item.productName?.split(', ').some((name) => name === 'Empty Product')
                                            ? 'warning'
                                            : 'success'
                                        : 'failed';
                                } else {
                                    statusClass = item.status === 'success' ? 'success' : 'failed';
                                }
                            }

                            const itemSx =
                                statusClass === 'success'
                                    ? { bgcolor: '#e6f9e6', borderColor: '#c3e6cb', color: '#155724' }
                                    : statusClass === 'warning'
                                      ? { bgcolor: '#fab75f', borderColor: '#d98f3b', color: '#000' }
                                      : { bgcolor: '#fdecea', borderColor: '#f5c6cb', color: '#721c24' };

                            return (
                                <Box
                                    component="li"
                                    key={index}
                                    data-testid={
                                        index === 0 ? 'mobile-history-item-last' : `mobile-history-item-${index}`
                                    }
                                    sx={{
                                        mb: '12px',
                                        p: '8px',
                                        borderRadius: '6px',
                                        border: '1px solid',
                                        ...itemSx,
                                    }}
                                >
                                    {/* header */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            mb: '4px',
                                        }}
                                    >
                                        <Typography sx={{ fontWeight: 'bold', fontSize: 14 }}>{item.eslMac}</Typography>
                                        <Typography sx={{ fontSize: 12, color: '#888' }}>({item.timestamp})</Typography>
                                    </Box>

                                    {/* status */}
                                    <Typography sx={{ fontSize: 14, lineHeight: 1.4 }}>
                                        {item.customMessage ? (
                                            <>
                                                <Box component="span" sx={{ fontWeight: 'bold', mr: '4px' }}>
                                                    [{t(item.status === 'success' ? 'Success' : 'Failed')}]
                                                </Box>
                                                {item.customMessage}
                                            </>
                                        ) : item.special_job_text === 'led-control' ? (
                                            <>
                                                <Box component="span" sx={{ fontWeight: 'bold', mr: '4px' }}>
                                                    [{t(item.status === 'success' ? 'Success' : 'Failed')}]
                                                </Box>
                                                {item.status === 'success' ? (
                                                    item.other_info?.toUpperCase() === 'BARCODE' ? (
                                                        t('LED Command Sent to all devices associated.')
                                                    ) : (
                                                        t('LED Command Sent to the device.')
                                                    )
                                                ) : (
                                                    <Box
                                                        component="span"
                                                        sx={{ fontStyle: 'italic', color: '#dc3545' }}
                                                    >
                                                        {item.errorReason ? `${item.errorReason}` : t('Unknown Error')}
                                                    </Box>
                                                )}
                                            </>
                                        ) : item.productBarcode ? (
                                            item.allSuccess ? (
                                                <>
                                                    <Box component="span" sx={{ fontWeight: 'bold', mr: '4px' }}>
                                                        [{t(hasEmptyProduct ? 'Success' : 'Success')}]
                                                    </Box>
                                                    {t('Paired with Product Barcode')} [
                                                    <Box component="strong">{processedBarcode}</Box>] [
                                                    {item.productName || 'N/A'}]
                                                </>
                                            ) : (
                                                <>
                                                    <Box component="span" sx={{ fontWeight: 'bold', mr: '4px' }}>
                                                        [{t('Failed')}]
                                                    </Box>
                                                    {t('Failed Association')} [
                                                    <Box component="strong">{processedBarcode}</Box>]
                                                    <Box component="strong">
                                                        [{item.productName?.trim() ? item.productName : 'Empty Product'}
                                                        ]
                                                    </Box>
                                                    <Box
                                                        component="span"
                                                        sx={{ fontStyle: 'italic', color: '#dc3545' }}
                                                    >
                                                        {item.errorReason ? `: ${item.errorReason}` : ''}
                                                    </Box>
                                                </>
                                            )
                                        ) : item.status === 'success' ? (
                                            <>
                                                <Box component="span" sx={{ fontWeight: 'bold', mr: '4px' }}>
                                                    [{t('Success')}]
                                                </Box>
                                                {t('Deassociated Successfully')}
                                            </>
                                        ) : (
                                            <>
                                                <Box component="span" sx={{ fontWeight: 'bold', mr: '4px' }}>
                                                    [{t('Failed')}]
                                                </Box>
                                                {t('Failed Deassociation')}
                                                <Box component="span" sx={{ fontStyle: 'italic', color: '#dc3545' }}>
                                                    {item.errorReason ? `: ${item.errorReason}` : ''}
                                                </Box>
                                            </>
                                        )}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                ) : (
                    <Typography variant="body2" sx={{ color: styleVariables.darkGrey, m: 0, textAlign: 'center' }}>
                        {t('No History')}
                    </Typography>
                )}
            </Box>
        </Box>
    );
};

export default History;
